import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { StateConfig } from 'components/charts/config'

import {
  ProductBalance,
  ProductTokenBalance,
  UserProductHistory,
} from '../../../services/query-v2/users/history/model'
import { loadable } from 'jotai/utils'

import Big from 'big.js'
import { userHistoriesFactory } from '../../../services/query-v2/users/history/index'
import {
  PortfolioChartData as PortfolioTimeSeries,
  PortfolioActionType,
  PortfolioStorage,
} from './interface'
import { ALL_PORTFOLIO_ID } from 'app-constants'
import { userAllDailyStatisticsFactory } from '../../../services/query-v2/products/daily-statistics/index'
import { atomEffect } from 'jotai-effect'
import { buildUserBalances, byProductAddress, plotChart } from './services'
import { DailyCapital } from 'services/query-v2/products/daily-statistics/model'
import { getDefaultChartInterval } from '../../../components/charts/config'
import { AnyWritableAtom } from 'components/scope-provider'
import { byInvestmentSize } from '../services'
import { userAccountAtom } from 'context/account'
import { zeroAddress } from 'viem'

const timeIntervalAtom = atom<StateConfig>({ name: '1 year', range: 31536000 })
const selectedProductSymbol = atom<string>(ALL_PORTFOLIO_ID)
const errorProducts = atom<string[]>([])
const portfolioDispatchAtom = atom(null, (_, set, action: PortfolioActionType) => {
  switch (action.type) {
    case 'set_selected_portfolio':
      set(selectedProductSymbol, action.payload)
      break
    case 'set_current_time_window':
      set(timeIntervalAtom, action.payload)
      break
    default:
      break
  }
})
const dailyStatisticsQueryAtom = userAllDailyStatisticsFactory()
const loadableDailyStatistics = loadable(dailyStatisticsQueryAtom.response)

const userProductHistoryAtom = userHistoriesFactory()
const loadableUserProductHistoryRequestAtom = loadable(userProductHistoryAtom.response)

const userProductBalancesAtom = atom<ProductBalance[]>((get) => {
  const loadableData = get(loadableUserProductHistoryRequestAtom)
  if (loadableData.state === 'hasError') {
    throw loadableData.error
  }
  if (loadableData.state === 'hasData') {
    return buildUserBalances(loadableData.data)
  } else {
    return []
  }
})

const isUserBalanceZero = atom<boolean>((get) => {
  const loadableData = get(loadableUserProductHistoryRequestAtom)
  if (loadableData.state === 'hasError') {
    throw loadableData.error
  }
  let zeroBalance = true
  if (loadableData.state === 'hasData') {
    for (const item of loadableData.data) {
      const bal =
        item.history && item.history.length === 0
          ? Big(0)
          : item.history[item.history.length - 1].balance
      zeroBalance = zeroBalance && bal.eq(0)
    }
  }

  return zeroBalance
})

const userHasBalanceHistory = atom<boolean>((get) => {
  const loadableData = get(loadableUserProductHistoryRequestAtom)
  if (loadableData.state === 'hasError') {
    throw loadableData.error
  }
  let hasBalance = false
  if (loadableData.state === 'hasData') {
    for (const item of loadableData.data) {
      if (!hasBalance) {
        hasBalance = item.history.some((item) => item.balance.gt(0))
      }
    }
  }
  return hasBalance
})
const filteredDailyStatistics = atom<DailyCapital[]>((get) => {
  const loadableDaily = get(loadableDailyStatistics)
  if (loadableDaily.state === 'hasError') {
    throw loadableDaily.error
  }
  if (loadableDaily.state === 'hasData') {
    const dailyStats = loadableDaily.data
    const selectedProductId = get(selectedProductSymbol)
    const filtered = dailyStats.filter(byProductAddress(selectedProductId))
    return filtered
  } else {
    return []
  }
})

const filteredUserHistory = atom<UserProductHistory[]>((get) => {
  const loadableData = get(loadableUserProductHistoryRequestAtom)
  if (loadableData.state === 'hasError') {
    throw loadableData.error
  }
  if (loadableData.state === 'hasData') {
    return loadableData.data.filter(byProductAddress(get(selectedProductSymbol)))
  } else {
    return []
  }
})

const userTokensAtom = atom<ProductTokenBalance[]>((get) => {
  const loadableData = get(loadableUserProductHistoryRequestAtom)
  if (loadableData.state === 'hasError') {
    throw loadableData.error
  }
  if (loadableData.state === 'hasData') {
    const balances = loadableData.data.map((product) => {
      const productBalance =
        product.history.length !== 0
          ? Big(product.history[product.history.length - 1].balance)
          : Big(0)
      return {
        id: product.id,
        tokens: productBalance,
      }
    })

    return [
      ...balances,
      {
        id: ALL_PORTFOLIO_ID,
        tokens: balances.reduce((acc, curr) => {
          return acc.add(curr.tokens)
        }, Big(0)),
      },
    ]
  }
  return []
})

const chartDataAtom = atom<PortfolioTimeSeries | null>((get) => {
  const dailyCapital = get(filteredDailyStatistics)
  const errorCapitals = dailyCapital.filter((el) => el.isError).map((el) => el.id)
  const userProductHistory = get(filteredUserHistory).filter((el) => !errorCapitals.includes(el.id))
  const sortedUserProductHistory = userProductHistory
    .filter((el) => el.history.length !== 0)
    .sort((a, b) => {
      if (a.history[0].timestamp < b.history[0].timestamp) return -1
      if (a.history[0].timestamp > b.history[0].timestamp) return 1
      return 0
    })
  if (dailyCapital.length !== 0 && sortedUserProductHistory.length !== 0) {
    return plotChart(dailyCapital, sortedUserProductHistory)
  } else {
    return null
  }
})

export const setAddressEffect = atomEffect((get, set) => {
  const address = get(userAccountAtom)
  if (address && address !== zeroAddress) {
    set(userProductHistoryAtom.userAddress, address)
  }
})
export const setErrors = atomEffect((get, set) => {
  const dailyCapital = get(filteredDailyStatistics)
  if (dailyCapital) {
    set(
      errorProducts,
      dailyCapital.filter((el) => el.isError).map((el) => el.id),
    )
  }
})

export const getPortfolioStorage = (): PortfolioStorage => {
  useAtom(setAddressEffect)
  useAtom(setErrors)

  return {
    isUserBalanceZero: useAtomValue(isUserBalanceZero),
    userHasBalanceHistory: useAtomValue(userHasBalanceHistory),
    timeWindow: useAtomValue(timeIntervalAtom),
    selectedProductId: useAtomValue(selectedProductSymbol),
    productBalances: useAtomValue(userProductBalancesAtom),
    userProductHistory: useAtomValue(userProductHistoryAtom.response).sort(byInvestmentSize),
    tokenBalances: useAtomValue(userTokensAtom),
    chartData: useAtomValue(chartDataAtom),
    errorProducts: useAtomValue(errorProducts),
    dispatch: useSetAtom(portfolioDispatchAtom),
  }
}

export const createPortfolioStorage = (): [AnyWritableAtom, unknown][] => {
  const userAddress = useAtomValue(userAccountAtom)

  const setUserAddressContractStatus = useSetAtom(userProductHistoryAtom.userAddress)
  setUserAddressContractStatus(userAddress ? userAddress : zeroAddress)

  const response: [AnyWritableAtom, unknown][] = [
    [timeIntervalAtom, getDefaultChartInterval()],
    [selectedProductSymbol, ALL_PORTFOLIO_ID],
    [userProductHistoryAtom.userAddress, userAddress],
  ]
  return response
}
